// User settings helpers shared by client hooks and server code
// (profiles.user_settings + profiles.profile_tools → UserSettings).
import { BUILTIN_TOOL_UUID, BUILTIN_UUID_TO_TOOL_ID } from "@/lib/builtin-tools";
import { DEFAULT_MODEL, isValidModelId, type ModelId } from "@/lib/model-id";
import type { ToolId, UserSettings } from "@/types/settings";
import { DEFAULT_VOICE, resolveVoice } from "@/lib/voices";

export const DEFAULT_USER_SETTINGS: UserSettings = {
  model: DEFAULT_MODEL,
  voice: DEFAULT_VOICE,
  tools: {
    webSearch: true,
    webFetch: true,
    memory: true,
    codeExecution: true,
    knowledgeBase: false,
    myFiles: false,
    searchProjectMeetingMinutes: false,
    searchProjectChats: false,
    projectRegisters: false,
    resolveDateTime: true,
    writingStyleProfile: false,
    // Always enabled — identity instruction; not user-controllable
    woodsIdentity: true,
  },
  preferences: "",
  resolveDateTimeConfig: {
    defaultTimezone: "Pacific/Auckland",
  },
};

export type ProfileToolEntry = { tool_id: string; config: unknown };

/** profile_tools array (presence = enabled) → Record<ToolId, boolean> */
export function profileToolsToRecord(
  profileTools: ProfileToolEntry[] | null | undefined,
): Record<ToolId, boolean> {
  if (!profileTools) return { ...DEFAULT_USER_SETTINGS.tools };

  const result: Record<ToolId, boolean> = {
    webSearch: false,
    webFetch: false,
    memory: false,
    codeExecution: false,
    knowledgeBase: false,
    myFiles: false,
    searchProjectMeetingMinutes: false,
    searchProjectChats: false,
    projectRegisters: false,
    resolveDateTime: false,
    writingStyleProfile: false,
    woodsIdentity: true, // Always on — identity instruction
  };
  for (const entry of profileTools) {
    const toolId = BUILTIN_UUID_TO_TOOL_ID[entry.tool_id];
    // knowledgeBase, searchProjectMeetingMinutes, searchProjectChats, projectRegisters are project-scoped — not enabled via profile_tools.
    if (
      toolId &&
      toolId !== "knowledgeBase" &&
      toolId !== "searchProjectMeetingMinutes" &&
      toolId !== "searchProjectChats" &&
      toolId !== "projectRegisters"
    ) {
      result[toolId] = true;
    }
  }
  return result;
}

/** Record<ToolId, boolean> → profile_tools array (only enabled, excludes knowledgeBase and woodsIdentity) */
export function recordToProfileTools(
  tools: Record<ToolId, boolean>,
): Array<{ tool_id: string; config: Record<string, never> }> {
  return (Object.entries(tools) as Array<[ToolId, boolean]>)
    .filter(
      ([toolId, enabled]) =>
        enabled &&
        toolId !== "knowledgeBase" &&
        toolId !== "searchProjectMeetingMinutes" &&
        toolId !== "searchProjectChats" &&
        toolId !== "projectRegisters" &&
        toolId !== "woodsIdentity",
    )
    .map(([toolId]) => ({ tool_id: BUILTIN_TOOL_UUID[toolId], config: {} }));
}

/** A profiles row (user_settings + profile_tools) → UserSettings, with defaults. */
export function userSettingsFromProfile(
  row: { user_settings: unknown; profile_tools: unknown } | null | undefined,
): UserSettings {
  if (!row) return { ...DEFAULT_USER_SETTINGS, tools: { ...DEFAULT_USER_SETTINGS.tools } };
  const us = row.user_settings as Record<string, unknown> | null;
  const model = isValidModelId(us?.model) ? (us!.model as ModelId) : DEFAULT_USER_SETTINGS.model;
  const tools = profileToolsToRecord(row.profile_tools as ProfileToolEntry[] | null);
  const preferences = typeof us?.preferences === "string" ? us.preferences : DEFAULT_USER_SETTINGS.preferences;
  const defaultTimezone =
    typeof us?.resolveDateTimeConfig === "object" &&
    us.resolveDateTimeConfig !== null &&
    typeof (us.resolveDateTimeConfig as { defaultTimezone?: unknown }).defaultTimezone === "string" &&
    (us.resolveDateTimeConfig as { defaultTimezone: string }).defaultTimezone.trim().length > 0
      ? (us.resolveDateTimeConfig as { defaultTimezone: string }).defaultTimezone
      : DEFAULT_USER_SETTINGS.resolveDateTimeConfig.defaultTimezone;
  return { model, voice: resolveVoice(us?.voice), tools, preferences, resolveDateTimeConfig: { defaultTimezone } };
}
